import { Skeleton } from "@mui/material";
import { useReferenceLabel } from "components/Form/components/ReferenceLabel/useReferenceLabel";
import RecordLabel from "components/RecordLabel";
import { FeAllType, FeByType, FeRecordType } from "config/config";
import customObjectConfig from "config/resources/custom_object";
import { getRelativeUrl } from "utils/service";
import { isUuid } from "utils/utils";

export interface SchemaFormReadonlyReferenceProps {
    elementSchema: any;
    value: any;
    dataPath: (string | number)[];
}

/**
 * Read-only display for a RECORD_REFERENCE (form-autocomplete) custom-object
 * attribute in list-view cells / dashboard record-table widgets.
 *
 * The interactive Autocomplete renders a bordered control box that reads as a
 * clickable button, and shows "Select an option" when empty — a false affordance
 * in read-only list views (there is no inline edit for references yet, see
 * operator-task-fe-inline-edit-list-views). Instead, render the referenced
 * record as plain linked text (RecordLabel), and show "No <type> selected" when
 * empty.
 *
 * The id -> name label is resolved through the shared, batched reference-label
 * resolver (useReferenceLabel): every read-only reference cell on a screen that
 * points at the same resource is resolved with ONE `{id in [...]}` query and a
 * single (resource, id)-keyed cache entry, rather than one single-id fetch and
 * one redux slice per cell. Name-stored references need no fetch — their value
 * is already the display name.
 */
export const SchemaFormReadonlyReference = ({
    elementSchema,
    value,
}: SchemaFormReadonlyReferenceProps) => {
    const recordType: string | undefined = elementSchema.record_type?.toLowerCase();
    const hasNameAsOptionValue =
        elementSchema.store === "record_name_existing" ||
        elementSchema.store === "record_name_new";

    const isEmpty = value == null || value === "";

    // An id-stored reference whose value is a UUID is resolved async (id -> name)
    // through the shared batched resolver. Name-stored references already carry
    // their display name, and non-UUID values are rendered as-is, so neither is
    // "resolving".
    const isResolvableId =
        !hasNameAsOptionValue && Boolean(recordType) && !isEmpty && isUuid(String(value));

    // Resolve the display label for id-stored references. Name-stored references
    // already carry the display name as their value, so we skip resolution
    // (resource === undefined) and render the value directly.
    const entry = useReferenceLabel(
        !hasNameAsOptionValue ? recordType : undefined,
        isEmpty ? undefined : String(value)
    );

    if (isEmpty) {
        const typeLabel =
            recordType && recordType !== customObjectConfig.resourceName
                ? recordType.replace(/_/g, " ")
                : null;
        const placeholder = typeLabel ? `No ${typeLabel} selected` : "No record selected";
        return <div className="schemaFormReadonlyText text-dark-grey">{placeholder}</div>;
    }

    // While an id-stored reference is still resolving to its name, show a loading
    // skeleton instead of leaking the raw UUID — which would otherwise be shown
    // on cold load and then flip to the resolved name a moment later. Once the
    // entry is resolved (even to a null label), fall through to the label logic
    // below, so an id that resolves to no name still shows the raw value rather
    // than a perpetual skeleton.
    if (isResolvableId && entry?.status !== "resolved") {
        return (
            <div className="schemaFormReadonlyText">
                <Skeleton variant="text" width={90} aria-label="Loading reference" />
            </div>
        );
    }

    const label = entry?.label ?? String(value);
    const by: FeByType = hasNameAsOptionValue ? "name" : "id";
    const link = recordType
        ? getRelativeUrl(recordType as FeAllType, String(value), by)
        : null;

    return (
        <RecordLabel
            name={label}
            link={link}
            iconByType={recordType as FeRecordType}
        />
    );
};
